import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GallerySection } from '@/mocks/galleryData';

interface GalleryGridProps {
  section: GallerySection;
}

export default function GalleryGrid({ section }: GalleryGridProps) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <section id={`section-${section.id}`} className="mb-20 scroll-mt-24">
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25]/10 border border-[#F57F25]/20 rounded-lg shrink-0">
            <i className={`${section.icon || 'ri-image-line'} text-[#F57F25] text-lg`}></i>
          </div>
          <div>
            <h2 className="text-white font-black text-xl md:text-2xl uppercase tracking-tight">
              {section.labelKey && t(section.labelKey) !== section.labelKey ? t(section.labelKey) : (section.title || section.name)}
            </h2>
            <p className="text-white/40 text-xs leading-snug max-w-sm">{section.subtitleKey && t(section.subtitleKey) !== section.subtitleKey ? t(section.subtitleKey) : section.subtitle}</p>
          </div>
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-[#F57F25]/30 to-transparent hidden sm:block"></div>
      </div>

      {/* Masonry-style grid */}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
        {section.images.map((img) => (
          <div
            key={img.id}
            className="break-inside-avoid group relative overflow-hidden rounded-xl cursor-pointer"
            onClick={() => setLightbox(img.src)}
          >
            <img
              src={img.src}
              alt={img.alt}
              className={`w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${
                img.span === 'wide' ? 'h-56 md:h-64' :
                img.span === 'tall' ? 'h-72 md:h-80' :
                'h-52 md:h-60'
              }`}
              loading="lazy"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-[#F57F25] rounded-full">
                  <i className="ri-zoom-in-line text-black text-lg"></i>
                </div>
                <p className="text-white text-xs font-semibold text-center px-4 leading-snug drop-shadow-lg">
                  {img.alt}
                </p>
              </div>
            </div>
            {/* Bottom gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-[#F57F25] rounded-full transition-colors cursor-pointer border-none"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <img
            src={lightbox}
            alt="Gallery preview"
            className="max-w-full max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
